import styled, { css } from 'styled-components';
import { breakpoints, touchFeedback, touchTarget } from '../../../styles/brand';

const accent = '#ff9800';
const ink = '#323232';
const muted = '#6b6b6b';
const line = '#e8e8e8';

export const Page = styled.div`
  ${touchFeedback}

  max-width: 76rem;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  font-family: 'RedHatDisplay-Regular';
  color: ${ink};
`;

/** Mesmo link de voltar da tela de detalhes do torneio. */
export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2.75rem;
  border: 0;
  background: none;
  padding: 0;
  font-family: 'RedHatDisplay-Bold';
  font-size: 1.1em;
  color: #323232;
  opacity: 0.8;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }

  &:active {
    opacity: 0.6;
  }
`;

export const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  margin: 0.6rem 0 1.4rem;

  h1 {
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(1.5rem, 3vw, 2.1rem);
    line-height: 1.1;
    letter-spacing: -0.01em;
    margin: 0;
  }

  p {
    margin: 0.3rem 0 0;
    color: ${muted};
    font-size: 0.9rem;
  }
`;

export const Progress = styled.div`
  font-family: 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  font-size: 0.95rem;
  color: ${ink};

  span {
    color: ${muted};
    font-family: 'RedHatDisplay-Regular';
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 22rem) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.8rem;

  button {
    border: 1px solid ${line};
    background: #fff;
    border-radius: 999px;
    min-height: 2.25rem;
    padding: 0.35rem 0.8rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.8rem;
    color: ${ink};
    cursor: pointer;
    touch-action: manipulation;
    transition:
      background 120ms ease-out,
      border-color 120ms ease-out;
  }

  button[aria-pressed='true'] {
    background: ${ink};
    border-color: ${ink};
    color: #fff;
  }

  button:active {
    transform: scale(0.97);
  }
`;

export const TeamList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${line};
  border-radius: 0.9rem;
  overflow: hidden;
  background: #fff;
`;

export const TeamItem = styled.li<{ $active: boolean }>`
  & + & {
    border-top: 1px solid ${line};
  }

  button {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.15rem 0.75rem;
    align-items: center;
    text-align: left;
    border: 0;
    min-height: ${touchTarget};
    padding: 0.75rem 0.9rem;
    touch-action: manipulation;
    background: ${({ $active }) => ($active ? '#fff7ec' : 'transparent')};
    box-shadow: ${({ $active }) =>
      $active ? `inset 3px 0 0 ${accent}` : 'none'};
    cursor: pointer;
    font: inherit;
    color: inherit;
  }

  button:active {
    background: #fff1dc;
  }

  strong {
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.95rem;
  }

  small {
    grid-column: 1;
    color: ${muted};
    font-size: 0.78rem;
  }
`;

export const Status = styled.span<{ $scored: boolean }>`
  grid-row: span 2;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  min-height: 1.9rem;
  padding: 0 0.65rem;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $scored }) =>
    $scored
      ? css`
          color: #146c2e;
          background: #e3f6e8;
        `
      : css`
          color: #8a6100;
          background: #fff4d6;
        `}
`;

export const Panel = styled.section`
  border: 1px solid ${line};
  border-radius: 1rem;
  background: #fff;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

export const PanelTitle = styled.div`
  h2 {
    font-family: 'RedHatDisplay-Bold';
    font-size: 1.3rem;
    line-height: 1.15;
    margin: 0;
  }

  p {
    margin: 0.2rem 0 0;
    color: ${muted};
    font-size: 0.85rem;
  }
`;

export const Timer = styled.div<{
  $phase: 'presentation' | 'questions' | 'over';
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-radius: 0.8rem;
  transition: background 200ms ease-out;
  background: ${({ $phase }) =>
    $phase === 'presentation'
      ? '#f4f6fb'
      : $phase === 'questions'
        ? '#fff4d6'
        : '#fde8ea'};
`;

export const TimerLabel = styled.div`
  font-size: 0.8rem;
  color: ${muted};

  strong {
    display: block;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1.9rem;
    line-height: 1;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${ink};
    margin-top: 0.15rem;
  }
`;

export const TimerActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const SmallButton = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ $primary }) => ($primary ? ink : line)};
  background: ${({ $primary }) => ($primary ? ink : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : ink)};
  border-radius: 0.6rem;
  min-height: ${touchTarget};
  padding: 0.45rem 0.9rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.85rem;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 100ms ease-out;

  &:active {
    transform: scale(0.97);
  }
`;

export const Criterion = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.4rem 1rem;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid ${line};

  h3 {
    font-family: 'RedHatDisplay-Bold';
    font-size: 0.95rem;
    margin: 0;
  }

  ul {
    grid-column: 1 / -1;
    margin: 0;
    padding-left: 1.1rem;
    color: ${muted};
    font-size: 0.8rem;
    line-height: 1.45;
  }
`;

export const ScoreControl = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 0.9rem;

  input[type='range'] {
    flex: 1;
    accent-color: ${accent};
    height: ${touchTarget};
    cursor: pointer;
    touch-action: manipulation;
  }
`;

export const ScoreBox = styled.input`
  width: 4rem;
  height: ${touchTarget};
  border: 1px solid ${line};
  /* 16px: o iOS não dá zoom na página ao focar o campo. */
  font-size: 1rem;
  border-radius: 0.55rem;
  text-align: center;
  font-family: 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  color: ${ink};
  outline: none;

  &:focus {
    border-color: ${accent};
    box-shadow: 0 0 0 3px rgba(255, 152, 0, 0.18);
  }
`;

export const Max = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  color: ${muted};
  white-space: nowrap;
`;

export const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid ${line};
`;

export const Total = styled.div`
  font-size: 0.85rem;
  color: ${muted};

  strong {
    font-family: 'RedHatDisplay-Bold';
    font-size: 2rem;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${ink};
    margin-right: 0.25rem;
  }
`;

export const Empty = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: ${muted};
  border: 1px dashed ${line};
  border-radius: 1rem;
`;

export const Note = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${muted};
`;
